import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { hydrateRoot } from 'react-dom/client';
import loadable, { LoadableComponent } from '@loadable/component';

import {
  getClientLocale,
  I18NProvider,
  I18NProviders,
  Languages,
} from '@utils/i18n';
import apolloWrapper from '@hoc/apolloWrapper';

export const dedicatedRenderContainers:
Record<string, LoadableComponent<unknown> | React.FC<unknown>> = {
  // NavBar is required to be initialized as fast as possible
  // so we can't allow to load it lazy
  NavBar: loadable(() => import('@containers/Header')),
  Footer: loadable(() => import('@components/Footer')),
  ReviewsInCatalog: loadable(() => import('@containers/ReviewsInCatalog')),
  SeoHubCatalog: loadable(() => import('@containers/SeoHubCatalog')),
  SeoHubTag: loadable(() => import('@containers/SeoHubTag')),
};

const hydrateComponents = (
  components: [HTMLDivElement, React.FC<unknown>][],
  basename: Languages | undefined,
  lang: Languages,
) => components.forEach(
  ([ssrBlock, Component]) => {
    const ComponentWithApollo = apolloWrapper(Component);
    setTimeout(
      () => (
        hydrateRoot(
          ssrBlock,
          <BrowserRouter basename={basename}>
            <I18NProvider lang={lang}>
              {
                () => (
                  <ComponentWithApollo />
                )
              }
            </I18NProvider>
          </BrowserRouter>,
        )
      ),
      0,
    );
  },
);

export const hydrateSSRBlocks = async (): Promise<void> => {
  const { lang, routerBasename } = getClientLocale();

  const ssrBlocks = document.querySelectorAll<HTMLDivElement>('[data-ssr-component-name]');

  let syncComponents: [HTMLDivElement, React.FC<unknown>][] = [];
  const asyncComponents: [HTMLDivElement, LoadableComponent<unknown>][] = [];

  // Find ssr blocks on page and get mapped components. Divide mapped
  // components on sync and async.
  Array.from(ssrBlocks).forEach(
    (ssrBlock) => {
      const componentName = ssrBlock.dataset.ssrComponentName;
      if (componentName) {
        const Component = dedicatedRenderContainers[componentName];
        if ('load' in Component) {
          asyncComponents.push([ssrBlock, Component]);
        } else {
          syncComponents.push([ssrBlock, Component]);
        }
      }
    },
  );

  if (syncComponents.length) {
    hydrateComponents(syncComponents, routerBasename, lang);
    syncComponents = [];
  }

  // Load async components concurrently and add loaded components
  // to sync components.
  // Motivation:
  // We can render async components(LoadableComponents) directly without preload
  // but in this case until component not downloaded it is empty and react
  // override rendered ssr component on empty component and after download it
  // render expected component. As a result user see content shift.
  await Promise.all(
    asyncComponents
      .map(
        async ([ssrBlock, AsyncComponent]: [HTMLDivElement, LoadableComponent<unknown>]) => {
          syncComponents.push([
            ssrBlock,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (await AsyncComponent.load() as any).default as React.FC<unknown>,
          ]);
        },
      )
      // Preload translations
      .concat(
        I18NProviders[lang]
          ? [I18NProviders[lang].load().then(() => undefined)]
          : [],
      ),
  );

  hydrateComponents(syncComponents, routerBasename, lang);
};
